import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import {
  findDuplicates,
  findDuplicatesByFields,
  type DuplicateResult,
} from '@/lib/documents/duplicate-detector';

interface DetectDuplicatesResponse {
  success: boolean;
  data?: DuplicateResult;
  error?: string;
}

/**
 * POST /api/documents/detect-duplicates
 *
 * Body (option A – existing document):
 *   { document_id: string }
 *
 * Body (option B – raw fields):
 *   { empresa_id, numero_factura, nif_emisor, total_factura, fecha }
 */
export default withAuth(async (req, res) => {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  try {
    const {
      document_id,
      empresa_id,
      numero_factura,
      nif_emisor,
      total_factura,
      fecha,
    } = req.body;

    let result: DuplicateResult;

    if (document_id) {
      // Option A: look up by existing document
      result = await findDuplicates(supabaseAdmin, document_id);
    } else if (empresa_id) {
      // Option B: raw field search
      result = await findDuplicatesByFields(supabaseAdmin, {
        empresa_id,
        numero_factura: numero_factura ?? null,
        nif_emisor: nif_emisor ?? null,
        total_factura: total_factura != null ? Number(total_factura) : null,
        fecha_factura: fecha ?? null,
      });
    } else {
      return res.status(400).json({
        success: false,
        error: 'Se requiere document_id o empresa_id para detectar duplicados.',
      });
    }

    return res.status(200).json({ success: true, data: result } as DetectDuplicatesResponse);
  } catch (error: any) {
    console.error('[detect-duplicates] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error al detectar duplicados.',
    } as DetectDuplicatesResponse);
  }
});
